import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RefreshCcw, ChevronRight, Shield } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-b from-[#E3F2FD] to-white pt-16 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mx-auto w-16 h-16 bg-[#F0F7FF] rounded-full flex items-center justify-center mb-6">
              <RefreshCcw className="h-8 w-8 text-[#5E60CE]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#5E60CE] mb-6 font-display">Refund & Cancellation Policy</h1>
            <p className="text-lg text-[#4F86F7] mb-6 font-display">Last Updated: April 3, 2025</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 font-display">
              <Link to="/" className="hover:text-[#5E60CE]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Refund & Cancellation Policy</span>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-[24px] shadow-lg p-6 sm:p-8 border border-[#E6EFFD]">
            <div className="prose prose-lg prose-headings:font-display prose-headings:text-[#5E60CE] prose-p:font-display prose-p:text-gray-600 prose-a:text-[#4F86F7] prose-a:font-display max-w-none">
              <p>
                At Kuchipuchi, we strive to ensure your complete satisfaction with our personalized children's books and services. This Refund & Cancellation Policy outlines the conditions under which refunds and cancellations are processed.
              </p>
              
              <h2>1. Digital Products</h2>
              
              <h3>1.1 Personalized Digital Stories</h3>
              <p>
                Due to the personalized nature of our digital stories, which are created specifically for you based on your input:
              </p>
              <ul>
                <li>All sales of digital stories are final and non-refundable once the personalization process has begun.</li>
                <li>If you have not yet started the personalization process, you may cancel your order for a full refund.</li>
                <li>If you experience technical issues that prevent you from accessing your digital story, please contact our support team for assistance.</li>
              </ul>
              
              <h3>1.2 Subscription Services</h3>
              <p>
                For subscription-based services:
              </p>
              <ul>
                <li>You may cancel your subscription at any time through your account settings.</li>
                <li>Cancellations will take effect at the end of your current billing cycle.</li>
                <li>No partial refunds are provided for unused portions of the current billing period.</li>
                <li>Annual subscriptions may be eligible for a prorated refund if cancelled within 30 days of purchase.</li>
              </ul>
              
              <h2>2. Physical Products</h2>
              
              <h3>2.1 Printed Books</h3>
              <p>
                For printed personalized books:
              </p>
              <ul>
                <li>Orders may be cancelled for a full refund before the book enters production.</li>
                <li>Once production has started, cancellations are not possible due to the personalized nature of the product.</li>
                <li>If you receive a damaged or defective book, please contact us within 14 days of receipt with photos of the damage. We will either replace the book or provide a refund.</li>
              </ul>
              
              <h3>2.2 Shipping Issues</h3>
              <p>
                For shipping-related concerns:
              </p>
              <ul>
                <li>If your order has not arrived within the estimated delivery timeframe, please contact our customer support.</li>
                <li>We are not responsible for packages lost or delayed due to incorrect shipping information provided by the customer.</li>
                <li>In the event a package is returned to us due to an undeliverable address, we will contact you to arrange reshipment or refund minus shipping costs.</li>
              </ul>
              
              <h2>3. Refund Process</h2>
              <p>
                When a refund is approved:
              </p>
              <ul>
                <li>An order can be cancelled up to 24 hours after placing it by sending us an email at <a href="mailto:support@kuchipuchi.com">support@kuchipuchi.com</a> and we shall refund the full amount paid in such cases.</li>
                <li>If the book is delivered in a damaged condition, you can request for a free replacement or full refund.</li>
                <li>Refunds will be processed to the original payment method used for the purchase.</li>
                <li>Refunds will take a maximum of 7 working days to process at our end. There might be additional time taken by your source account to reflect the credit.</li>
                <li>In case you do not get the refund amount even after 14 working days, you can contact us at <a href="mailto:support@kuchipuchi.com">support@kuchipuchi.com</a>.</li>
              </ul>
              
              <h2>4. Exceptional Circumstances</h2>
              <p>
                We understand that exceptional circumstances can occur. If you have an issue that isn't covered by this policy, please contact our customer support team, and we'll work with you to find a fair resolution.
              </p>
              
              <h2>5. Changes to This Policy</h2>
              <p>
                We may update this Refund & Cancellation Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last Updated" date.
              </p>
              
              <h2>6. Contact Us</h2>
              <p>
                If you have any questions about this Refund & Cancellation Policy, please contact us at:
              </p>
              <p>
                <a href="mailto:support@kuchipuchi.com">support@kuchipuchi.com</a>
              </p>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12 p-6 bg-[#F0F7FF] rounded-[24px] border border-[#E6EFFD] shadow-md"
          >
            <div className="flex items-start gap-4">
              <div className="mt-1 bg-white p-2 rounded-full">
                <Shield className="h-6 w-6 text-[#5E60CE]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#5E60CE] font-display mb-2">Customer Satisfaction</h3>
                <p className="text-gray-600 font-display">
                  Your happiness matters to us. If you have any concerns about your purchase, please don't hesitate to <a href="mailto:support@kuchipuchi.com" className="text-[#4F86F7] font-bold hover:underline">contact our support team</a>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Simple decorated footer */}
      <div className="py-10 bg-[#F8FBFF] border-t border-[#E6EFFD]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 font-display mb-4">kuchipuchi — Magical stories for magical children</p>
          <div className="w-20 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] mx-auto rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy; 